import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import { tr } from '@/contexts/LanguageContext';

// 1D (oct-2026) — galería a pantalla completa de las fotos/videos de un servicio (video silenciado por defecto).
export const ServiceGallery = ({ service, startIndex = 0, onClose }) => {
  const items = service?.media || [];
  const [idx, setIdx] = useState(Math.min(startIndex, Math.max(0, items.length - 1)));
  const [muted, setMuted] = useState(true);
  const touch = useRef(null);
  const cur = items[idx];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(items.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items.length, onClose]);

  if (!cur) return null;
  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    touch.current = null;
    if (dx < -40) setIdx((i) => Math.min(items.length - 1, i + 1));
    if (dx > 40) setIdx((i) => Math.max(0, i - 1));
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] bg-black flex flex-col" data-testid="service-gallery" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="flex items-center justify-between px-4 pt-[max(env(safe-area-inset-top),12px)] pb-2 text-white">
        <div className="min-w-0">
          <p className="text-sm font-bold truncate" data-testid="service-gallery-title">{service.name}</p>
          <p className="text-[11px] text-white/60 tabular-nums" data-testid="service-gallery-counter">{idx + 1} / {items.length}</p>
        </div>
        <button type="button" onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" aria-label={tr("Cerrar")} data-testid="service-gallery-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {cur.kind === 'video' ? (
          <video key={cur.url} src={cur.url} poster={cur.thumbnail_url || undefined} autoPlay loop muted={muted} playsInline controls={false}
            className="max-h-full max-w-full object-contain" data-testid="service-gallery-video" />
        ) : (
          <img key={cur.url} src={cur.url} alt={service.name} className="max-h-full max-w-full object-contain" data-testid="service-gallery-image" />
        )}
        {cur.kind === 'video' && (
          <button type="button" onClick={() => setMuted((m) => !m)} className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label={muted ? tr("Activar sonido") : tr("Silenciar")} data-testid="service-gallery-mute">
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        )}
        {idx > 0 && (
          <button type="button" onClick={() => setIdx(idx - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center" aria-label={tr("Anterior")} data-testid="service-gallery-prev"><ChevronLeft className="w-6 h-6" /></button>
        )}
        {idx < items.length - 1 && (
          <button type="button" onClick={() => setIdx(idx + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center" aria-label={tr("Siguiente")} data-testid="service-gallery-next"><ChevronRight className="w-6 h-6" /></button>
        )}
      </div>
      {items.length > 1 && (
        <div className="flex justify-center gap-1.5 pb-[max(env(safe-area-inset-bottom),16px)] pt-3" data-testid="service-gallery-dots">
          {items.map((m, i) => <span key={m.url} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/40'}`} />)}
        </div>
      )}
    </div>,
    document.body,
  );
};

/** Portada del servicio (foto o miniatura del video). Devuelve null si no hay medios: sin espacio vacío. */
export const serviceCover = (svc) => {
  const items = svc?.media || [];
  if (!items.length) return null;
  const m = items[Math.min(svc.cover_index || 0, items.length - 1)];
  return { ...m, src: m.kind === 'video' ? (m.thumbnail_url || null) : m.url };
};

export default ServiceGallery;
